import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="filters"
export default class extends Controller {
  static targets = [
    "form", 
    "activeFilters"
  ]

  connect() {
    // Initialize change tracking
    this.hasChanged = false
    this.updateActiveFilters()
  }
  
  // Update range filter with values from the range slider controller
  updateRangeFilter(filterParam, minValue, maxValue, initializing = false) {
    this.removeHiddenInputs(filterParam)
    
    if (minValue) {
      this.createHiddenInput(`${filterParam}_min`, minValue)
    }
    
    if (maxValue) {
      this.createHiddenInput(`${filterParam}_max`, maxValue)
    }
    
    this.hasChanged = !initializing
    this.updateActiveFilters()
  }
  
  // Update date filter with start/end values
  updateDateFilter(filterParam, startValue, endValue, initializing = false) {
    this.removeHiddenInputs(filterParam)
    
    if (startValue) {
      this.createHiddenInput(`${filterParam}_start`, startValue)
    }
    
    if (endValue) {
      this.createHiddenInput(`${filterParam}_end`, endValue)
    }
    
    this.hasChanged = !initializing
    this.updateActiveFilters()
  }
  
  // Update multi-select filter with selected values
  updateMultiFilter(filterParam, values, initializing = false) {
    this.removeHiddenInputs(filterParam)
    
    if (values && values.length > 0) {
      this.createHiddenInput(filterParam, values.join(','))
    }
    
    this.hasChanged = !initializing
    this.updateActiveFilters()
  }
  
  // Update text filter with a value
  updateTextFilter(filterParam, value, initializing = false) {
    this.removeHiddenInputs(filterParam)
    
    if (value) {
      this.createHiddenInput(filterParam, value)
    }
    
    this.hasChanged = !initializing
    this.updateActiveFilters()
  }
  
  // Helper method to create a hidden input
  createHiddenInput(name, value) {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    this.formTarget.appendChild(input)
    return input
  }
  
  // Helper method to remove hidden inputs by name
  // Uses ^= selector to match inputs whose name starts with the given prefix
  removeHiddenInputs(namePrefix) {
    const inputs = this.formTarget.querySelectorAll(`input[name^="${namePrefix}"]`)
    inputs.forEach(input => input.remove())
  }
  
  // Update the active filters display
  updateActiveFilters() {
    if (!this.hasActiveFiltersTarget) return
    
    const activeFilters = this.activeFiltersTarget
    // Clear existing active filters
    activeFilters.innerHTML = ''
    
    // Get all active filters
    const filterElements = []
    
    // Get all form inputs
    const formInputs = Array.from(this.formTarget.elements)
    
    // Define filter mappings (parameter name to display name)
    const filterMappings = {
      'city': 'City',
      'state': 'State',
      'scenario': 'Scenario',
      'decision_makers': 'Decision Makers',
      'market_value': 'Est. Market Value',
      'date': 'Date Added',
      'address': 'Address'
    }
    
    // Process multi-select filters (comma-separated values)
    for (const param of ['city', 'state', 'scenario']) {
      const input = formInputs.find(input => input.name === param)
      if (input && input.value) {
        const values = input.value.split(',').join(', ')
        filterElements.push({ 
          type: param, 
          name: filterMappings[param], 
          value: values 
        })
      }
    }
    
    // Process range filters
    for (const param of ['decision_makers', 'market_value']) {
      const minInput = formInputs.find(input => input.name === `${param}_min`)
      const maxInput = formInputs.find(input => input.name === `${param}_max`)
      
      if (minInput || maxInput) {
        let rangeText = ''
        
        if (minInput && maxInput) {
          if (param === 'market_value') {
            rangeText = `$${parseInt(minInput.value).toLocaleString()} - $${parseInt(maxInput.value).toLocaleString()}`
          } else {
            rangeText = `${minInput.value} - ${maxInput.value}`
          }
        } else if (minInput) {
          if (param === 'market_value') {
            rangeText = `Min: $${parseInt(minInput.value).toLocaleString()}`
          } else {
            rangeText = `Min: ${minInput.value}`
          }
        } else if (maxInput) {
          if (param === 'market_value') {
            rangeText = `Max: $${parseInt(maxInput.value).toLocaleString()}`
          } else {
            rangeText = `Max: ${maxInput.value}`
          }
        }
        
        filterElements.push({ 
          type: param, 
          name: filterMappings[param], 
          value: rangeText 
        })
      }
    }
    
    // Process date filters
    const startInput = formInputs.find(input => input.name === 'date_start')
    const endInput = formInputs.find(input => input.name === 'date_end')
    
    if (startInput || endInput) {
      let dateText = ''
      
      if (startInput && endInput && startInput.value && endInput.value) {
        dateText = `${this.formatDate(startInput.value)} - ${this.formatDate(endInput.value)}`
      } else if (startInput && startInput.value) {
        dateText = `From: ${this.formatDate(startInput.value)}`
      } else if (endInput && endInput.value) {
        dateText = `Until: ${this.formatDate(endInput.value)}`
      }
      
      if (dateText) {
        filterElements.push({ 
          type: 'date', 
          name: filterMappings['date'], 
          value: dateText 
        })
      }
    }
    
    // Text search
    const addressInput = this.formTarget.querySelector('input[name="address"]')
    if (addressInput && addressInput.value.trim()) {
      filterElements.push({ type: 'address', name: 'Address', value: addressInput.value })
    }
    
    // Display active filters
    if (filterElements.length > 0) {
      activeFilters.classList.remove('hidden')
      
      filterElements.forEach(filter => {
        const filterItem = document.createElement('div')
        filterItem.classList.add('inline-flex', 'items-center', 'px-3', 'py-1', 'rounded-full', 'bg-gray-100', 'text-sm', 'mr-2', 'mb-2')
        filterItem.innerHTML = `
          <span class="font-medium text-gray-700">${filter.name}: </span>
          <span class="ml-1 text-gray-900">${filter.value}</span>
          <button type="button" class="ml-2 text-gray-500 hover:text-gray-700 cursor-pointer" data-action="click->filters#clearFilter" data-filter-param="${filter.type}">×</button>
        `
        activeFilters.appendChild(filterItem)
      })
      
      // Add clear all button
      const clearAllButton = document.createElement('button')
      clearAllButton.type = 'button'
      clearAllButton.textContent = 'Clear All'
      clearAllButton.dataset.action = 'click->filters#clearAllFilters'
      clearAllButton.classList.add('ml-auto', 'text-indigo-600', 'hover:text-indigo-800', 'text-sm', 'font-medium', 'px-3', 'py-1', 'cursor-pointer')
      
      activeFilters.appendChild(clearAllButton)
    } else {
      activeFilters.classList.add('hidden')
    }
  }

  // Clear a specific filter
  // Can be called with an event object
  clearFilter(event) {
    const filterParam = event.currentTarget.dataset.filterParam
    this.removeHiddenInputs(filterParam)

    this.updateActiveFilters()
    this.submitForm()
  }

  // Clear all filters and reset the form
  clearAllFilters() {
    this.formTarget.reset()
    
    const inputs = this.formTarget.querySelectorAll('input[name][type="hidden"]');
    inputs.forEach(input => input.remove());
    
    if (this.hasActiveFiltersTarget) {
      this.activeFiltersTarget.innerHTML = ''
    }
    
    this.updateActiveFilters()
    this.submitForm()
  }

  // Submit the form to apply filters
  submitForm() {   
    // Parse the form's action URL to get existing parameters
    const actionUrl = new URL(this.formTarget.action, window.location.origin);
    const actionParams = new URLSearchParams(actionUrl.search);
    
    // Get form data
    const formData = new FormData(this.formTarget);
    const formParams = new URLSearchParams();
    
    // First, add all parameters from the form action URL
    for (const [name, value] of actionParams.entries()) {
      formParams.append(name, value);
    }
    
    // Then, add or update with form data (form data takes precedence)
    for (const [name, value] of formData.entries()) {
      if (value.trim() === '') continue;
      // Overwrite existing params with form values
      formParams.set(name, value);
    }
    
    // Build the final URL with the combined parameters
    const baseUrl = actionUrl.pathname;
    let url = baseUrl + (formParams.toString() ? '?' + formParams.toString().replace(/%2C/g, ',') : '');
    
    // Dispatch a standard submit event on the form before navigation
    const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
    
    // Prevent the default form submission
    const preventDefaultSubmit = (e) => {
      e.preventDefault();
      this.formTarget.removeEventListener('submit', preventDefaultSubmit);
    };
    this.formTarget.addEventListener('submit', preventDefaultSubmit);
    
    // Dispatch the event
    this.formTarget.dispatchEvent(submitEvent);
    
    // Navigate to the new URL
    window.location.href = url;
  }
      
  // Apply changes if they exist
  applyChangesIfNeeded() {
    if (this.hasChanged) {
      this.hasChanged = false
      this.submitForm()
      return true
    }
    return false
  }
  
  // Helper to format dates for display
  formatDate(dateString) {
    if (!dateString) return ''
    
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    })
  }
}
